import { IconButton, Typography } from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import { minutesToHours } from "date-fns"
import { ArrowRightCircle, User } from "iconoir-react"
import { useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"

import { API } from "@/api"
import { APIQueries } from "@/api/queries"
import { Organization } from "@/api/resources/organization/types"
import { DataTable } from "@/components/data-table"
import { SortConfig } from "@/components/data-table/types"
import PageContainer from "@/components/page-container"
import { useGlobalContext } from "@/context/global-context"
import { getByPath, renderAnalyticsMetric } from "@/features/teachers/utils"
import { useClubId } from "@/lib/hooks/use-club-id"
import routes, { routeBuilder } from "@/lib/routes"

const Learning = () => {
  const params = {
    fields: ["name", "teachers_count", "learning_analytics"],
  }

  const clubId = useClubId()
  const { organization } = useGlobalContext()

  const navigate = useNavigate()
  const orgListQuery = useQuery({
    queryKey: APIQueries.organization.list(params).queryKey,
    queryFn: () =>
      API.organization.list({
        params: params,
      }),
    select: (data) =>
      data.map((item) =>
        item.id.toString() === clubId
          ? { ...item, name: "Directly Enrolled Teachers" }
          : item,
      ),
  })

  const [query, setQuery] = useState<{
    search?: string
    sort?: SortConfig | null
  }>({
    search: "",
  })

  const handleQueryChange = useCallback(
    (params: { search?: string; sort?: SortConfig | null }) => {
      setQuery((prev) => ({
        ...prev,
        ...params,
      }))
    },
    [],
  )

  const filteredData = useMemo(() => {
    const orgList = orgListQuery.data
    if (!orgList || orgList.length === 0) return []

    const search = query.search?.toLowerCase().trim()
    let result = orgList

    if (search) {
      result = result.filter((org) => {
        const haystack = [
          org.name,
          org.teachersCount?.toString(),
          org.learningAnalytics?.totalLearningDurationInMinutes
            ? Math.floor(
                minutesToHours(
                  org.learningAnalytics.totalLearningDurationInMinutes,
                ),
              ).toString()
            : null,
          org.learningAnalytics?.averageLearningDurationInMinutes
            ? Math.floor(
                minutesToHours(
                  org.learningAnalytics.averageLearningDurationInMinutes,
                ),
              ).toString()
            : null,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()

        return haystack.includes(search)
      })
    }

    return result
  }, [orgListQuery.data, query.search])

  const sortedData = useMemo(() => {
    if (!query.sort) return filteredData

    const { key, direction } = query.sort

    const compare = (a: Organization, b: Organization) => {
      const aVal = getByPath(a, key)
      const bVal = getByPath(b, key)

      if (aVal == null && bVal == null) return 0
      if (aVal == null) return direction === "asc" ? -1 : 1
      if (bVal == null) return direction === "asc" ? 1 : -1

      // numbers (learning minutes, teachersCount)
      if (typeof aVal === "number" && typeof bVal === "number") {
        return direction === "asc" ? aVal - bVal : bVal - aVal
      }

      // strings (name)
      if (typeof aVal === "string" && typeof bVal === "string") {
        return direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
      }

      // fallback
      const aStr = String(aVal)
      const bStr = String(bVal)

      return direction === "asc"
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr)
    }

    return [...filteredData].sort(compare)
  }, [filteredData, query.sort])

  return (
    <PageContainer>
      <div className="flex flex-col">
        <>
          <DataTable<Organization>
            showSearch
            onRowClick={(row) => {
              navigate(
                routeBuilder(routes.dashboard.teachers, {
                  clubId: clubId,
                  orgId: row.id.toString(),
                }),
                {
                  state: { from: location.pathname },
                },
              )
            }}
            searchPlaceholder="Search Entities"
            data={sortedData}
            headerClassName="bg-onSurface-900 text-surface"
            onQueryChange={handleQueryChange}
            loading={orgListQuery.isLoading}
            actionSlot={
              <div className="flex flex-row items-center justify-between">
                <div className="flex flex-row items-center gap-0.25 rounded-full border border-onSurface-200 bg-onSurface-50 px-1.5 py-1.25">
                  <User height={20} width={20} className="text-onSurface-600" />
                  <Typography
                    variant={"largeBody"}
                    className="text-onSurface-600"
                  >
                    Total Teachers •{" "}
                    <span className="text-lg leading-[130%] tracking-tight text-onSurface-900">
                      {organization?.data?.teachersCount}
                    </span>
                  </Typography>
                </div>
              </div>
            }
            columns={[
              {
                key: "name",
                label: "Name",
                sortable: true,
                width: "300px",
                render: (val: string) => {
                  if (!val) return "-"
                  return (
                    <Typography className="text-[14px] font-semibold leading-[130%] tracking-normal text-onSurface-700">
                      {val}
                    </Typography>
                  )
                },
              },
              {
                width: "180px",
                key: "teachersCount",
                label: "Number of Teachers",
                sortable: true,
                render: (val: number) => {
                  if (!val) return "-"
                  return <Typography variant="smallBody">{val}</Typography>
                },
              },
              {
                width: "140px",
                key: "learningAnalytics.totalLearningDurationInMinutes",
                label: `Total Learning Hours`,
                sortable: true,
                render: (val: number) => {
                  if (!val) return "-"
                  return (
                    <>
                      {renderAnalyticsMetric({
                        value: val,
                        render: ({ formattedValue }) => (
                          <Typography className="text-[14px] font-semibold leading-[130%] tracking-normal text-onSurface-700">
                            {formattedValue}
                          </Typography>
                        ),
                        renderInTime: true,
                      })}
                    </>
                  )
                },
              },
              {
                width: "140px",
                key: "learningAnalytics.averageLearningDurationInMinutes",
                label: `Avg. Learning Hours`,
                sortable: true,
                render: (val: number) => {
                  if (!val) return "-"
                  return (
                    <>
                      {renderAnalyticsMetric({
                        value: val,
                        render: ({ formattedValue }) => (
                          <Typography className="text-[14px] font-semibold leading-[130%] tracking-normal text-onSurface-700">
                            {formattedValue}
                          </Typography>
                        ),
                        renderInTime: true,
                      })}
                    </>
                  )
                },
              },

              {
                key: "actions",
                width: "80px",
                label: "",
                render: () => (
                  <IconButton>
                    <ArrowRightCircle
                      className="size-3.5 shrink-0 text-onSurface-500"
                      strokeWidth={2}
                    />
                  </IconButton>
                ),
                align: "center",
              },
            ]}
          />
        </>
      </div>
    </PageContainer>
  )
}

export default Learning
